import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaMailBulk } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import LanguageSwitcher from "../components/LanguageSwitcher";

export default function OnBoarding() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Get temp user from localStorage
  const tempUser = JSON.parse(localStorage.getItem("tempUser")) || {};
  const name = tempUser.firstName || "User";
  const email = tempUser.email || "hello@hellogate.com";

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [cooldown, setCooldown] = useState(0); // countdown in seconds

  const handleResend = async () => {
    if (cooldown > 0) return; // prevent clicking when blocked

    try {
      setLoading(true);
      setMessage("");

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/resend-verify-email`,
        { email },
        { headers: { lang: i18n.language } }
      );

      setMessage(t("emailResentSuccess"));

      // save expiry time to localStorage
      const expiry = Date.now() + 60000; // 60 seconds
      localStorage.setItem("resendCooldown", expiry.toString());

      setCooldown(60); // start countdown
    } catch (err) {
      console.error(err);
      setMessage(t("emailResentError"));
    } finally {
      setLoading(false);
    }
  };

  // Load cooldown from localStorage on mount
  useEffect(() => {
    const expiry = localStorage.getItem("resendCooldown");
    if (expiry) {
      const remaining = Math.floor((+expiry - Date.now()) / 1000);
      if (remaining > 0) {
        setCooldown(remaining);
      } else {
        localStorage.removeItem("resendCooldown");
      }
    }
  }, []);

  // Timer effect
  useEffect(() => {
    if (cooldown <= 0) return;

    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem("resendCooldown"); // clear when finished
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldown]);

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-6 text-gray-800"
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
    >
      <div className="absolute top-6 ltr:right-6 rtl:left-6">{/* <LanguageSwitcher /> */}</div>

      {/* Email Icon & Title */}
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-purple-100 text-purple-600 shadow-md">
          <FaMailBulk size={40} />
        </div>
        <h1 className="text-center text-2xl font-bold text-gray-900 sm:text-3xl">{t("checkYourEmail")}</h1>
        <p className="max-w-md text-center text-gray-600">{t("weSentLink", { email })}</p>
      </div>

      {/* Actions */}
      <div className="mt-8 flex flex-col items-center gap-4 text-center">
        <button
          onClick={() => navigate("/auth/login")}
          className="w-full max-w-xs rounded-lg bg-purple-500 px-6 py-3 font-semibold text-white shadow transition hover:bg-purple-600"
        >
          {t("openLogin")}
        </button>

        <button
          onClick={handleResend}
          disabled={loading || cooldown > 0}
          className="text-sm font-medium text-purple-600 hover:underline disabled:opacity-50"
        >
          {loading
            ? t("resending")
            : cooldown > 0
              ? t("resendEmailIn", { seconds: cooldown })
              : t("resendEmail")}
        </button>
      </div>

      {message && <p className="mt-4 text-center text-sm text-gray-600">{message}</p>}
    </div>
  );
}
